import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Search, Mail, Phone, ImageIcon, ArrowUpRight, Loader2 } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { fetchAdminSummary, fetchQuotes, fetchConsultations } from "@/lib/api";
import { Input } from "@/components/ui/input";

const STATUS_COLORS = {
  new: "text-[color:var(--primary)] border-[color:var(--primary)]/50 bg-[color:var(--primary)]/5",
  quoted: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
  closed: "text-white/40 border-white/15 bg-white/[0.02]",
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span className={`inline-block font-mono-accent text-[9px] tracking-[0.28em] uppercase px-2 py-1 border ${cls}`}>
      {status}
    </span>
  );
}

function RequestCard({ req, type }) {
  const to = type === "quote" ? `/admin/quotes/${req.id}` : `/admin/consultations/${req.id}`;
  const created = req.created_at
    ? (typeof req.created_at === "string" ? new Date(req.created_at) : req.created_at)
    : null;
  return (
    <Link to={to} className="group block border border-white/10 bg-[#0C1017] p-5 md:p-6 hover:border-[color:var(--primary)]/60 transition-colors" data-testid={`admin-${type}-card-${req.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-display uppercase text-white text-xl md:text-2xl leading-tight tracking-tight">{req.name}</div>
          <div className="mt-1 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">
            {type === "quote" ? (req.service || "—") : (req.service_interest || "Consultation")}
          </div>
        </div>
        <StatusBadge status={req.status || "new"} />
      </div>
      <div className="space-y-1.5 text-sm text-white/60">
        <div className="flex items-center gap-2 truncate"><Mail size={12} className="text-[color:var(--primary)]/70 flex-shrink-0" /> {req.email}</div>
        <div className="flex items-center gap-2"><Phone size={12} className="text-[color:var(--primary)]/70 flex-shrink-0" /> {req.phone}</div>
        {type === "consultation" && req.preferred_date && (
          <div className="text-white/70 text-sm mt-2">Preferred: {req.preferred_date} {req.preferred_time ? `· ${req.preferred_time}` : ""}</div>
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] text-white/40">
          {req.photos && req.photos.length > 0 && (
            <span className="inline-flex items-center gap-1 text-white/60"><ImageIcon size={11} /> {req.photos.length}</span>
          )}
          {created && (
            <span>{format(created, "MMM d, yyyy · h:mm a")}</span>
          )}
        </div>
        <ArrowUpRight size={14} className="text-white/40 group-hover:text-[color:var(--primary)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
      </div>
    </Link>
  );
}

export default function AdminDashboard({ type = "quotes" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => {
      setLoading(true);
      try {
        const [s, list] = await Promise.all([
          fetchAdminSummary(),
          type === "quotes" ? fetchQuotes() : fetchConsultations(),
        ]);
        if (!m) return;
        setSummary(s);
        setItems(list);
      } finally {
        m && setLoading(false);
      }
    })();
    return () => { m = false; };
  }, [type]);

  const filtered = items.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(s)
        || (r.email || "").toLowerCase().includes(s)
        || (r.phone || "").toLowerCase().includes(s)
        || (r.service || r.service_interest || "").toLowerCase().includes(s);
  });

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-accent text-[10px] tracking-[0.35em] uppercase text-[color:var(--primary)] mb-2">
            {type === "quotes" ? "Quote Requests" : "Consultation Bookings"}
          </div>
          <h1 className="font-display uppercase text-white text-4xl md:text-5xl tracking-tight leading-none">
            {type === "quotes" ? "Quotes" : "Consultations"}
          </h1>
        </div>
        {summary && (
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <div className="border border-white/10 bg-[#0C1017] px-4 py-3">
              <div className="font-mono-accent text-[9px] tracking-[0.25em] uppercase text-white/40">New Quotes</div>
              <div className="font-display uppercase text-2xl md:text-3xl text-[color:var(--primary)]">{summary.quotes.new}</div>
            </div>
            <div className="border border-white/10 bg-[#0C1017] px-4 py-3">
              <div className="font-mono-accent text-[9px] tracking-[0.25em] uppercase text-white/40">New Bookings</div>
              <div className="font-display uppercase text-2xl md:text-3xl text-[color:var(--primary)]">{summary.consultations.new}</div>
            </div>
            <div className="border border-white/10 bg-[#0C1017] px-4 py-3">
              <div className="font-mono-accent text-[9px] tracking-[0.25em] uppercase text-white/40">Estimates Sent</div>
              <div className="font-display uppercase text-2xl md:text-3xl text-white">{summary.estimates.total}</div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, phone, service…"
          data-testid="admin-search"
          className="pl-9 bg-[#0C1017] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-0 rounded-none h-10"
        />
      </div>

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center text-white/50">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="border border-dashed border-white/10 py-24 text-center text-white/50"
        >
          No {type === "quotes" ? "quote requests" : "consultations"} yet.
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <RequestCard key={r.id} req={r} type={type === "quotes" ? "quote" : "consultation"} />
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
